import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Lightbulb, ExternalLink, Trash2, Edit3, Save, X, Calendar, Tag, FileText, AlertCircle, Loader2 } from 'lucide-react';
import { getIdeas, updateIdea, deleteIdea } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { formatDisplayDateWithMonth } from '../utils/dateUtils';

const MyIdeasPage = () => {
  const { user } = useAuth();
  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editNote, setEditNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  useEffect(() => {
    fetchIdeas();
  }, []);

  const fetchIdeas = async () => {
    try {
      setLoading(true);
      const data = await getIdeas();
      setIdeas(data);
    } catch (err) {
      setError('Failed to load your saved ideas');
      console.error('Error fetching ideas:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (idea) => {
    setEditingId(idea.id);
    setEditNote(idea.personal_note || '');
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditNote('');
  };

  const handleSaveNote = async (ideaId) => {
    setSavingNote(true);
    try {
      const updated = await updateIdea(ideaId, { personal_note: editNote });
      setIdeas(ideas.map(idea => 
        idea.id === ideaId ? updated : idea
      ));
      setEditingId(null);
      setEditNote('');
    } catch (err) {
      console.error('Error saving note:', err);
    } finally {
      setSavingNote(false);
    }
  };

  const handleDeleteClick = (ideaId) => {
    setConfirmDelete(ideaId);
  };

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return;
    
    setDeletingId(confirmDelete);
    try {
      await deleteIdea(confirmDelete);
      setIdeas(ideas.filter(idea => idea.id !== confirmDelete));
    } catch (err) {
      console.error('Error deleting idea:', err);
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Sleep': 'bg-indigo-100 text-indigo-700',
      'Vitamins': 'bg-orange-100 text-orange-700',
      'Stress': 'bg-pink-100 text-pink-700',
      'CLL': 'bg-blue-100 text-blue-700',
      'Nutrition': 'bg-green-100 text-green-700',
      'Treatment': 'bg-purple-100 text-purple-700',
      'Wellbeing': 'bg-teal-100 text-teal-700'
    };
    return colors[category] || 'bg-slate-100 text-slate-700';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-8 max-w-4xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-3 bg-amber-100 rounded-xl">
            <Lightbulb className="h-8 w-8 text-amber-600" />
          </div>
          <div>
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight text-slate-900">
              My Ideas & Research
            </h1>
            <p className="text-slate-600 mt-1">
              Your personal thinking and memory space
            </p>
          </div>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
        <div className="flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Personal Space</p>
            <p className="text-sm text-amber-700">
              This is your private list of topics to explore or revisit. These are not medical recommendations — just your own thoughts and interests.
            </p>
          </div>
        </div>
      </div>

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Empty State - First time user onboarding */}
      {!error && ideas.length === 0 && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 p-8 text-center border-b border-amber-100">
            <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-5 shadow-sm">
              <Lightbulb className="h-10 w-10 text-amber-500" />
            </div>
            <h3 className="text-2xl font-bold text-slate-900 mb-2">Add your first saved idea</h3>
            <p className="text-slate-600 max-w-md mx-auto">
              This is your personal space to save thoughts, questions, and things you want to remember.
            </p>
          </div>
          
          <div className="p-6 md:p-8">
            <h4 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-4">How to use this</h4>
            
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-sm">1</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Browse the site</p>
                  <p className="text-sm text-slate-600">Read articles in CLL/SLL News, Blog, or Education pages</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-sm">2</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Save what interests you</p>
                  <p className="text-sm text-slate-600">Click "Save to My Ideas" on any page to add it here</p>
                </div>
              </div>
              
              <div className="flex items-start gap-4">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-amber-700 font-bold text-sm">3</span>
                </div>
                <div>
                  <p className="font-medium text-slate-900">Add your own notes</p>
                  <p className="text-sm text-slate-600">Write questions for your doctor, or things you want to revisit later</p>
                </div>
              </div>
            </div>
            
            <div className="mt-6 pt-6 border-t border-slate-100">
              <p className="text-sm text-slate-500 text-center">
                <span className="font-medium">Tip:</span> Use this to prepare for appointments — save topics and add questions you want to ask.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Ideas List */}
      {ideas.length > 0 && (
        <div className="space-y-4">
          {ideas.map((idea) => (
            <div
              key={idea.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:border-slate-300 transition-colors"
            >
              <div className="p-5">
                {/* Header Row */}
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-slate-900 mb-2">
                      {idea.title}
                    </h3>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className={`px-2.5 py-1 rounded-full font-medium ${getCategoryColor(idea.category)}`}>
                        <Tag className="h-3 w-3 inline mr-1" />
                        {idea.category}
                      </span>
                      <span className="text-slate-500 flex items-center gap-1">
                        <Calendar className="h-3.5 w-3.5" />
                        {formatDisplayDateWithMonth(idea.date_saved, user?.country)}
                      </span>
                    </div>
                  </div>
                  
                  {/* Actions */}
                  <div className="flex items-center gap-2">
                    <Link
                      to={idea.source_url}
                      className="p-2 text-slate-400 hover:text-primary hover:bg-primary/10 rounded-lg transition-colors"
                      title="Go to source page"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Link>
                    <button
                      onClick={() => handleDeleteClick(idea.id)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Remove from list"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                {/* Source Link */}
                <div className="flex items-center gap-2 text-sm text-slate-500 mb-4">
                  <FileText className="h-4 w-4" />
                  <span>From: </span>
                  <Link 
                    to={idea.source_url} 
                    className="text-primary hover:underline"
                  >
                    {idea.source_page}
                  </Link>
                </div>

                {/* Personal Note Section */}
                <div className="bg-slate-50 rounded-xl p-4">
                  {editingId === idea.id ? (
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Personal Note
                      </label>
                      <textarea
                        value={editNote}
                        onChange={(e) => setEditNote(e.target.value)}
                        placeholder="Add your thoughts, questions, or reminders..."
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                        rows={3}
                      />
                      <div className="flex justify-end gap-2 mt-3">
                        <button
                          onClick={handleCancelEdit}
                          className="px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-200 rounded-lg transition-colors"
                        >
                          <X className="h-4 w-4 inline mr-1" />
                          Cancel
                        </button>
                        <button
                          onClick={() => handleSaveNote(idea.id)}
                          disabled={savingNote}
                          className="px-3 py-1.5 text-sm bg-primary text-white rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50"
                        >
                          {savingNote ? (
                            <Loader2 className="h-4 w-4 inline mr-1 animate-spin" />
                          ) : (
                            <Save className="h-4 w-4 inline mr-1" />
                          )}
                          Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-slate-700">Personal Note</span>
                        <button
                          onClick={() => handleStartEdit(idea)}
                          className="text-xs text-primary hover:underline flex items-center gap-1"
                        >
                          <Edit3 className="h-3 w-3" />
                          {idea.personal_note ? 'Edit' : 'Add note'}
                        </button>
                      </div>
                      {idea.personal_note ? (
                        <p className="text-sm text-slate-600 whitespace-pre-wrap">
                          {idea.personal_note}
                        </p>
                      ) : (
                        <p className="text-sm text-slate-400 italic">
                          No notes yet. Click "Add note" to add your thoughts.
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900 mb-2">Remove this idea?</h3>
            <p className="text-slate-600 mb-6">
              This will remove the item from your saved ideas list. You can always save it again later.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="px-4 py-2 text-slate-600 hover:bg-slate-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={deletingId}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              >
                {deletingId ? (
                  <Loader2 className="h-4 w-4 inline mr-2 animate-spin" />
                ) : null}
                Remove
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyIdeasPage;
